<template>
  <v-container fluid class="crm">
    <p class="text-center title mt-6 mb-2">CRM</p>
    <v-tabs v-model="section" show-arrows centered class="crm__sections" @change="sectionChanged">
      <v-tab v-for="item in sections" :key="item.value">{{ item.title }}</v-tab>
    </v-tabs>

    <CrmLeads v-if="sectionKey === 'leads'" />
    <CrmReports v-else-if="sectionKey === 'reports'" />
    <CrmActions v-else-if="sectionKey === 'actions'" />
    <CrmBroadcasts v-else-if="sectionKey === 'broadcasts'" />
    <CrmBotChats v-else-if="sectionKey === 'chats'" />
    <div v-else>
    <p class="text-center grey--text mt-4 mb-4">
      Кожна зміна статусу записується з датою і причиною. Натисніть на рядок, щоб побачити історію, змінити статус або додати нотатку.
    </p>

    <v-tabs v-model="tab" show-arrows class="crm__tabs" @change="reload">
      <v-tab v-for="item in tabs" :key="item.value">{{ item.title }} ({{ countOf(item.value) }})</v-tab>
    </v-tabs>

    <div class="crm__filters">
      <v-text-field
        v-model="search"
        label="Пошук: ПІБ, email, телефон"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        clearable
        hide-details
        @input="reloadLater"
      ></v-text-field>
      <v-autocomplete
        v-model="country"
        :items="countryItems"
        item-text="name"
        item-value="code"
        label="Країна"
        outlined
        dense
        clearable
        hide-details
        @change="reload"
      ></v-autocomplete>
      <v-select
        v-model="city"
        :items="cityItems"
        item-text="name"
        item-value="slug"
        label="Місто"
        outlined
        dense
        clearable
        hide-details
        @change="reload"
      ></v-select>
      <v-select
        v-model="source"
        :items="sourceItems"
        label="Джерело"
        outlined
        dense
        clearable
        hide-details
        @change="reload"
      ></v-select>
      <v-select
        v-model="contacts"
        :items="contactsItems"
        label="Контакти в анкеті"
        outlined
        dense
        clearable
        hide-details
        @change="reload"
      ></v-select>
      <v-menu v-for="field in dateFields" :key="field.key" v-model="field.open" :close-on-content-click="false" offset-y min-width="auto">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="humanDate(dates[field.key])"
            :label="field.label"
            prepend-inner-icon="mdi-calendar"
            readonly
            outlined
            dense
            clearable
            hide-details
            v-bind="attrs"
            v-on="on"
            @click:clear="setDate(field, null)"
          ></v-text-field>
        </template>
        <v-date-picker
          :value="dates[field.key]"
          locale="uk-UA"
          :first-day-of-week="1"
          no-title
          @input="setDate(field, $event)"
        ></v-date-picker>
      </v-menu>
      <v-select
        v-model="sort"
        :items="sorts"
        label="Сортування"
        outlined
        dense
        hide-details
        @change="reload"
      ></v-select>
      <v-btn color="primary" depressed :loading="exporting" @click="downloadExcel">Excel</v-btn>
      <v-btn color="primary" outlined :loading="importing" @click="$refs.importFile.click()">Імпорт з таблиці</v-btn>
      <input ref="importFile" type="file" accept=".xlsx,.xls,.csv" class="d-none" @change="importTable">
    </div>

    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      :server-items-length="total"
      :options.sync="options"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100], itemsPerPageText: 'Рядків на сторінці' }"
      loading-text="Завантаження…"
      no-data-text="Нікого не знайдено"
      class="crm__table"
      @click:row="open"
    >
      <template v-slot:[`item.full_name`]="{ item }">
        <a v-if="item.public_url" :href="item.public_url" target="_blank" rel="noopener" @click.stop>{{ item.full_name }}</a>
        <span v-else>{{ item.full_name }}</span>
      </template>
      <template v-slot:[`item.cities`]="{ item }">{{ item.cities.join(", ") }}</template>
      <template v-slot:[`item.status`]="{ item }">
        <div :class="`crm__status crm__status--${item.status}`">{{ item.status_label }}</div>
        <div class="caption grey--text">{{ humanDateTime(item.status_at) }}</div>
        <div v-if="item.status_reason" class="caption">{{ item.status_reason }}</div>
      </template>
      <template v-slot:[`item.tariff`]="{ item }">
        <div>{{ item.tariff || "—" }}</div>
        <div v-if="item.paid_at" class="caption grey--text">оплата {{ humanDate(item.paid_at) }}</div>
        <div v-if="item.paid_until" class="caption grey--text">до {{ humanDate(item.paid_until) }}</div>
      </template>
      <template v-slot:[`item.source`]="{ item }">
        <div class="crm__source">{{ item.source || "—" }}</div>
      </template>
      <template v-slot:[`item.registered_at`]="{ item }">{{ humanDate(item.registered_at) }}</template>
      <template v-slot:[`item.views`]="{ item }">{{ item.views }} / {{ item.contacts_clicks }}</template>
      <template v-slot:[`item.leads`]="{ item }">
        <div>{{ item.leads }}</div>
        <div v-if="item.leads" class="caption grey--text">відп. {{ item.leads_answered }}, конс. {{ item.leads_scheduled }}</div>
      </template>
    </v-data-table>
    </div>

    <v-dialog v-model="importDialog" max-width="560">
      <v-card v-if="importResult">
        <v-card-title>Імпорт з таблиці</v-card-title>
        <v-card-text>
          <p>Знайдено на сайті й оновлено: {{ importResult.matched }}.</p>
          <template v-if="importResult.unmatched.length">
            <p>Не знайдено серед зареєстрованих ({{ importResult.unmatched.length }}):</p>
            <div v-for="row in importResult.unmatched" :key="row.row" class="caption">
              рядок {{ row.row }}: {{ row.name }} {{ row.phone }} {{ row.email }}
            </div>
          </template>
          <p class="caption mt-4 mb-0">Таблиця: перший рядок — назви колонок (ПІБ, Телефон, Email, Джерело, Тариф, Примітка). Психолога шукаємо за email або телефоном.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="importDialog = false">Закрити</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="640">
      <v-card v-if="current">
        <v-card-title class="crm__dialog-title">{{ current.full_name }}</v-card-title>
        <v-card-subtitle>
          {{ current.phone }}<span v-if="current.phone && current.email"> · </span>{{ current.email }}
        </v-card-subtitle>
        <v-card-text>
          <p v-if="current.unsubscribed_at" class="orange--text">
            Відписався {{ humanDate(current.unsubscribed_at) }}: {{ current.unsubscribe_reason }}
          </p>
          <p class="caption grey--text">
            Згода на обробку даних: {{ current.consent_at ? humanDateTime(current.consent_at) : "не зафіксована (реєстрація до появи згоди)" }}
          </p>
          <v-autocomplete
            v-model="workCountry"
            :items="countryItems"
            item-text="name"
            item-value="code"
            label="Країна роботи (розділ каталогу)"
            :loading="savingCountry"
            outlined
            dense
            hide-details
            class="mb-4"
            @change="saveWorkCountry"
          ></v-autocomplete>
          <p class="font-weight-bold mb-1">Контакти на сайті (кнопка «Контакти»)</p>
          <p class="caption mb-1">
            Психолог вказав сам: {{ ownContactsText || "нічого" }}
          </p>
          <v-switch
            v-model="publicContacts.show_account_phone"
            :disabled="!current.phone || !!current.own_contacts.phone"
            :loading="savingContacts"
            :label="`Показувати телефон з реєстрації${current.phone ? ': ' + current.phone : ''}`"
            class="mt-1"
            dense
            hide-details
            @change="savePublicContacts"
          ></v-switch>
          <v-switch
            v-model="publicContacts.show_account_email"
            :disabled="!current.email || !!current.own_contacts.email"
            :loading="savingContacts"
            :label="`Показувати пошту з реєстрації${current.email ? ': ' + current.email : ''}`"
            class="mt-1 mb-2"
            dense
            hide-details
            @change="savePublicContacts"
          ></v-switch>
          <p v-if="current.own_contacts.phone || current.own_contacts.email" class="caption grey--text">
            Якщо психолог сам вказав телефон чи пошту, на сайті показуються саме вони.
          </p>
          <p class="caption mb-6">На сайті зараз: {{ publicContactsText || "контактів немає, кнопки «Контакти» не видно" }}</p>

          <p class="font-weight-bold mb-2">Нотатка</p>
          <v-textarea v-model="note" outlined dense rows="2" auto-grow hide-details></v-textarea>
          <v-btn class="mt-2 mb-6" small depressed :loading="savingNote" @click="saveNote">Зберегти нотатку</v-btn>

          <!-- one message to this psychologist (e.g. what to fix in the profile): the own bot or e-mail, and the cabinet -->
          <p class="font-weight-bold mb-1">Написати психологу</p>
          <p class="caption mb-2">
            {{ current.telegram_linked ? "Прийде в Telegram-бот Платформи і в кабінет." : "Бот не підключено: прийде на пошту і в кабінет." }}
          </p>
          <v-textarea
            v-model="message.text"
            placeholder="Наприклад, що треба виправити в анкеті"
            outlined
            dense
            rows="2"
            auto-grow
            counter="3000"
            maxlength="3000"
            :error-messages="message.error"
          ></v-textarea>
          <v-btn class="mb-6" small depressed color="primary" :loading="message.sending" :disabled="!message.text.trim()" @click="sendMessage">
            Надіслати
          </v-btn>
          <span v-if="message.sent" class="caption ml-2">Надіслано</span>

          <p class="font-weight-bold mb-2">Змінити статус</p>
          <v-select
            v-model="change.status"
            :items="statusItems"
            label="Новий статус"
            outlined
            dense
          ></v-select>
          <v-textarea
            v-model="change.reason"
            label="Причина (обов'язково)"
            outlined
            dense
            rows="2"
            auto-grow
            counter="500"
            :error-messages="changeError"
          ></v-textarea>
          <p class="caption grey--text">
            «Активна» = схвалити й показувати в каталозі; без оплати анкета залишиться неактивною. «Неактивна» = деактивувати (або повернути з архіву). «Архів» = психолог відписався або перевести вручну.
          </p>
          <v-btn color="primary" depressed :loading="changing" :disabled="!change.status" @click="saveStatus">Зберегти статус</v-btn>

          <p class="font-weight-bold mt-6 mb-2">Історія статусів</p>
          <p v-if="historyLoading" class="grey--text">Завантаження…</p>
          <p v-else-if="!history.length" class="grey--text">Змін ще не було.</p>
          <div v-for="(item, index) in history" :key="index" class="crm__history">
            <div class="caption grey--text">{{ humanDateTime(item.created_at) }} · {{ sourceLabel(item.source) }}</div>
            <div>
              <span v-if="item.status_from">{{ statusLabel(item.status_from) }} → </span>{{ statusLabel(item.status_to) }}
            </div>
            <div v-if="item.reason" class="caption">{{ item.reason }}</div>
          </div>

          <p class="font-weight-bold mt-6 mb-2">Видалити персональні дані на вимогу</p>
          <p class="caption">Акаунт і анкета видаляються назавжди разом із заявками до психолога. Напишіть ВИДАЛИТИ, щоб підтвердити.</p>
          <div class="d-flex crm__erase">
            <v-text-field v-model="eraseConfirm" outlined dense hide-details placeholder="ВИДАЛИТИ"></v-text-field>
            <v-btn color="error" depressed :disabled="eraseConfirm !== 'ВИДАЛИТИ'" :loading="erasing" @click="erase">Видалити</v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Закрити</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";
import { countryOptions } from "@/utils/countries";
import CrmLeads from "@/components/dashboard/adminDashboard/crm/CrmLeads";
import CrmReports from "@/components/dashboard/adminDashboard/crm/CrmReports";
import CrmActions from "@/components/dashboard/adminDashboard/crm/CrmActions";
import CrmBroadcasts from "@/components/dashboard/adminDashboard/crm/CrmBroadcasts";
import CrmBotChats from "@/components/dashboard/adminDashboard/crm/CrmBotChats";

const STATUS_LABELS = { new: "Нова", active: "Активна", inactive: "Неактивна", archive: "Архів" };
const SOURCE_LABELS = { admin: "адмін", system: "автоматично", payment: "оплата", psychologist: "психолог" };

export default {
  name: "crmAdmin",
  components: { CrmLeads, CrmReports, CrmActions, CrmBroadcasts, CrmBotChats },
  data() {
    const sections = [
      { value: "psychologists", title: "Психологи" },
      { value: "leads", title: "Заявки" },
      { value: "reports", title: "Звіти" },
      { value: "broadcasts", title: "Розсилки" },
      { value: "chats", title: "Чати бота" },
      { value: "actions", title: "Журнал дій" },
    ];
    return {
      sections,
      section: Math.max(0, sections.findIndex((item) => item.value === this.$route.query.tab)),
      sourceItems: [
        { header: "Відповідь «Як дізналися»" },
        { value: "google", text: "Google" },
        { value: "instagram", text: "Instagram" },
        { value: "facebook", text: "Facebook" },
        { value: "ads", text: "Реклама" },
        { value: "recommendation", text: "Рекомендація" },
        { value: "other", text: "Інше" },
        { header: "Звідки прийшов на сайт" },
        { value: "other_site", text: "Інший сайт" },
        { value: "direct", text: "Прямий захід" },
      ],
      note: "",
      message: { text: "", sending: false, sent: false, error: "" },
      savingNote: false,
      contacts: "",
      contactsItems: [
        { value: "none", text: "Без контактів" },
        { value: "any", text: "З контактами" },
      ],
      publicContacts: { show_account_phone: false, show_account_email: false },
      savingContacts: false,
      eraseConfirm: "",
      erasing: false,
      importing: false,
      importDialog: false,
      importResult: null,
      tabs: [
        { value: "active", title: "Активні анкети" },
        { value: "inactive", title: "Неактивні анкети" },
        { value: "archive", title: "Архів" },
        { value: "new", title: "Нові" },
        { value: "", title: "Усі" },
      ],
      tab: 0,
      search: "",
      city: null,
      country: null,
      countryItems: countryOptions(),
      workCountry: "UA",
      savingCountry: false,
      source: "",
      dates: { from: null, to: null, paidFrom: null, paidTo: null },
      dateFields: [
        { key: "from", label: "Реєстрація від", open: false },
        { key: "to", label: "Реєстрація до", open: false },
        // a placement payment in the period (customer 30.10)
        { key: "paidFrom", label: "Оплата від", open: false },
        { key: "paidTo", label: "Оплата до", open: false },
      ],
      sort: "registered_desc",
      sorts: [
        { value: "registered_desc", text: "Спершу нові реєстрації" },
        { value: "registered_asc", text: "Спершу старі реєстрації" },
        { value: "status_desc", text: "Остання зміна статусу" },
        { value: "paid_desc", text: "Оплачено до" },
        { value: "views_desc", text: "Перегляди анкети" },
        { value: "leads_desc", text: "Кількість заявок" },
      ],
      headers: [
        { text: "ПІБ", value: "full_name", sortable: false },
        { text: "Телефон", value: "phone", sortable: false },
        { text: "Email", value: "email", sortable: false },
        { text: "Місто", value: "cities", sortable: false },
        { text: "Статус", value: "status", sortable: false, width: 220 },
        { text: "Тариф", value: "tariff", sortable: false },
        { text: "Джерело", value: "source", sortable: false },
        { text: "Реєстрація", value: "registered_at", sortable: false },
        { text: "Перегляди / кліки", value: "views", sortable: false },
        { text: "Заявки", value: "leads", sortable: false },
      ],
      rows: [],
      total: 0,
      counts: {},
      cityItems: [],
      options: { page: 1, itemsPerPage: 50 },
      loading: false,
      exporting: false,
      timer: null,
      dialog: false,
      current: null,
      history: [],
      historyLoading: false,
      change: { status: null, reason: "" },
      changeError: "",
      changing: false,
      statusItems: [
        { value: "active", text: "Активна" },
        { value: "inactive", text: "Неактивна" },
        { value: "archive", text: "Архів" },
      ],
    };
  },
  computed: {
    ownContactsText() {
      return this.current ? this.contactsText(this.current.own_contacts) : "";
    },
    publicContactsText() {
      return this.current ? this.contactsText(this.current.public_contacts) : "";
    },
    sectionKey() {
      return this.sections[this.section] ? this.sections[this.section].value : "psychologists";
    },
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
  },
  methods: {
    sectionChanged() {
      const tab = this.sectionKey === "psychologists" ? undefined : this.sectionKey;
      if (this.$route.query.tab !== tab) this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {});
      if (this.sectionKey === "psychologists") this.$nextTick(this.load);
    },
    saveWorkCountry() {
      const id = this.current.id;
      this.savingCountry = true;
      ApiClient.post(`api/admin/crm/psychologists/${id}/work-country`, { work_country: this.workCountry })
        .then((response) => {
          if (!response) return;
          const row = this.rows.find((item) => item.id === id);
          [row, this.current].forEach((item) => {
            if (item) item.work_country = response.data.data.work_country;
          });
        })
        .catch(() => {
          this.workCountry = this.current.work_country;
        })
        .finally(() => {
          this.savingCountry = false;
        });
    },
    savePublicContacts() {
      const id = this.current.id;
      this.savingContacts = true;
      ApiClient.post(`api/admin/crm/psychologists/${id}/public-contacts`, this.publicContacts)
        .then((response) => {
          if (!response) return;
          const data = response.data.data;
          const row = this.rows.find((item) => item.id === id);
          [row, this.current].forEach((item) => {
            if (!item) return;
            item.show_account_phone = data.show_account_phone;
            item.show_account_email = data.show_account_email;
            item.public_contacts = data.public_contacts;
          });
        })
        .catch(() => {
          this.publicContacts = { show_account_phone: this.current.show_account_phone, show_account_email: this.current.show_account_email };
        })
        .finally(() => {
          this.savingContacts = false;
        });
    },
    contactsText(contacts) {
      const labels = { phone: "телефон", email: "пошта", website: "сайт" };
      return Object.keys(contacts || {})
        .map((key) => `${labels[key] || key} ${contacts[key]}`)
        .join(", ");
    },
    sendMessage() {
      this.message.sending = true;
      this.message.error = "";
      ApiClient.post("api/admin/crm/broadcasts", {
        title: "Повідомлення від Платформи",
        text: this.message.text.trim(),
        channels: this.current.telegram_linked ? ["bot", "cabinet"] : ["email", "cabinet"],
        profile: this.current.id,
      })
        .then((response) => {
          if (!response) return;
          this.message.text = "";
          this.message.sent = true;
        })
        .catch((error) => {
          this.message.error = (error && error.response && error.response.data && error.response.data.message) || "Не вдалося надіслати";
        })
        .finally(() => {
          this.message.sending = false;
        });
    },
    saveNote() {
      this.savingNote = true;
      ApiClient.post(`api/admin/crm/psychologists/${this.current.id}/note`, { note: this.note })
        .then((response) => {
          if (!response) return;
          this.current.note = response.data.data.note;
          const row = this.rows.find((item) => item.id === this.current.id);
          if (row) row.note = this.current.note;
        })
        .finally(() => {
          this.savingNote = false;
        });
    },
    erase() {
      this.erasing = true;
      ApiClient.delete(`api/admin/crm/psychologists/${this.current.id}/personal-data`, { data: { confirm: this.eraseConfirm } })
        .then((response) => {
          if (!response) return;
          this.dialog = false;
          Vue.swal({ target: document.getElementById("main"), text: "Дані видалено", icon: "success", timer: 3000, toast: true, position: "top-right", showConfirmButton: false });
          this.load();
        })
        .catch((error) => {
          Vue.swal({ target: document.getElementById("main"), text: (error && error.message) || "Не вдалося видалити", icon: "error", timer: 6000, toast: true, position: "top-right", showConfirmButton: false });
        })
        .finally(() => {
          this.erasing = false;
        });
    },
    importTable(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      const data = new FormData();
      data.append("file", file);
      this.importing = true;
      ApiClient.post("api/admin/crm/psychologists/import", data)
        .then((response) => {
          if (!response) return;
          this.importResult = response.data.data;
          this.importDialog = true;
          this.load();
        })
        .catch((error) => {
          Vue.swal({ target: document.getElementById("main"), text: (error && error.message) || "Не вдалося імпортувати", icon: "error", timer: 6000, toast: true, position: "top-right", showConfirmButton: false });
        })
        .finally(() => {
          this.importing = false;
        });
    },
    params() {
      const status = this.tabs[this.tab] ? this.tabs[this.tab].value : "";
      const params = {
        status,
        search: this.search || "",
        city: this.city || "",
        country: this.country || "",
        source: this.source || "",
        contacts: this.contacts || "",
        registered_from: this.dates.from || "",
        registered_to: this.dates.to || "",
        paid_from: this.dates.paidFrom || "",
        paid_to: this.dates.paidTo || "",
        sort: this.sort,
      };
      Object.keys(params).forEach((key) => params[key] === "" && delete params[key]);
      return params;
    },
    load() {
      this.loading = true;
      const params = { ...this.params(), page: this.options.page, per_page: this.options.itemsPerPage };
      return ApiClient.get("api/admin/crm/psychologists", { params })
        .then((response) => {
          if (!response) return;
          this.rows = response.data.data;
          this.total = response.data.meta.total;
          this.counts = response.data.meta.counts;
          this.cityItems = response.data.meta.cities;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reload() {
      if (this.options.page !== 1) {
        this.options = { ...this.options, page: 1 };
      } else {
        this.load();
      }
    },
    reloadLater() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.reload, 400);
    },
    countOf(status) {
      if (!status) return Object.values(this.counts).reduce((sum, value) => sum + value, 0);
      return this.counts[status] || 0;
    },
    setDate(field, value) {
      this.dates[field.key] = value;
      field.open = false;
      this.reload();
    },
    downloadExcel() {
      this.exporting = true;
      ApiClient.get("api/admin/crm/psychologists/export", { params: this.params(), responseType: "blob" })
        .then((response) => {
          if (!response) return;
          const link = document.createElement("a");
          link.href = URL.createObjectURL(response.data);
          link.download = "crm-psychologists-" + new Date().toISOString().slice(0, 10) + ".xlsx";
          link.click();
          setTimeout(() => URL.revokeObjectURL(link.href), 1000);
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    open(item) {
      this.current = item;
      this.note = item.note || "";
      this.message = { text: "", sending: false, sent: false, error: "" };
      this.publicContacts = { show_account_phone: item.show_account_phone, show_account_email: item.show_account_email };
      this.workCountry = item.work_country || "UA";
      this.eraseConfirm = "";
      this.change = { status: null, reason: "" };
      this.changeError = "";
      this.dialog = true;
      this.loadHistory();
    },
    loadHistory() {
      this.historyLoading = true;
      this.history = [];
      return ApiClient.get(`api/admin/crm/psychologists/${this.current.id}/history`)
        .then((response) => {
          if (response) this.history = response.data.data;
        })
        .finally(() => {
          this.historyLoading = false;
        });
    },
    saveStatus() {
      const reason = (this.change.reason || "").trim();
      if (reason.length < 3) {
        this.changeError = "Вкажіть причину";
        return;
      }
      this.changeError = "";
      this.changing = true;
      ApiClient.post(`api/admin/crm/psychologists/${this.current.id}/status`, { status: this.change.status, reason })
        .then((response) => {
          if (!response) return;
          this.current = response.data.data;
          this.change = { status: null, reason: "" };
          Vue.swal({
            target: document.getElementById("main"),
            text: "Статус збережено",
            icon: "success",
            timer: 3000,
            toast: true,
            position: "top-right",
            showConfirmButton: false,
          });
          this.loadHistory();
          this.load();
        })
        .catch((error) => {
          const errors = error && error.response && error.response.data && error.response.data.errors;
          this.changeError = errors && errors.reason ? errors.reason[0] : "Не вдалося зберегти";
        })
        .finally(() => {
          this.changing = false;
        });
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    sourceLabel(source) {
      return SOURCE_LABELS[source] || source;
    },
    humanDate(value) {
      if (!value) return "";
      const date = value.length === 10 ? new Date(value + "T00:00:00") : new Date(value);
      return date.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    humanDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("uk-UA", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style lang="scss">
.crm__filters {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  align-items: center;
  margin: 16px 0;
}
.crm__table tbody tr {
  cursor: pointer;
}
.crm__table td {
  vertical-align: top;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.crm__status {
  font-weight: 600;
}
.crm__status--active {
  color: #00897b;
}
.crm__status--inactive {
  color: #ef6c00;
}
.crm__status--archive {
  color: #757575;
}
.crm__status--new {
  color: #1976d2;
}
.crm__source {
  max-width: 220px;
  white-space: normal;
}
.crm__erase {
  gap: 12px;
}
.crm__dialog-title {
  word-break: break-word;
}
.crm__history {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}
</style>
